import { useEffect, useState } from "react";
import { Minus, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  clampPerGroup,
  stepPerGroup,
  type PerGroupLimits,
} from "../utils/perGroupSizing";

interface RespondentStepperProps {
  id: string;
  label: string;
  value: number;
  limits: PerGroupLimits;
  onChange: (value: number) => void;
  disabled?: boolean;
  "data-testid"?: string;
}

/**
 * The sizing control: a big left-aligned number on the same underlined field the
 * study name uses, with the two steps at its right end.
 *
 * Interaction copied from Quick Study's `TwinsStepper`: typing is free and only
 * clamped on commit, so a half-typed value never snaps under the caret, and the
 * `+` goes inert at the ceiling instead of a sentence explaining the cap.
 */
export function RespondentStepper({
  id,
  label,
  value,
  limits,
  onChange,
  disabled = false,
  "data-testid": testId = "respondent-stepper",
}: RespondentStepperProps) {
  const [draft, setDraft] = useState(String(value));

  // The value also moves from outside the field (design resolution, a smaller
  // audience). Follow it whenever it is not mid-edit.
  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commit = (raw: string): number => {
    const digits = raw.replace(/[^\d]/g, "");
    const next = clampPerGroup(digits === "" ? limits.min : Number(digits), limits);
    onChange(next);
    return next;
  };

  const step = (direction: 1 | -1) => {
    const next = stepPerGroup(value, direction, limits);
    setDraft(String(next));
    onChange(next);
  };

  return (
    <div className="rounded-xl">
      <label
        htmlFor={id}
        className="text-sm text-foreground dark:text-white block mb-0"
      >
        {label}
      </label>
      <div className="border-b border-border py-2 flex items-center justify-between gap-4">
        <input
          id={id}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={draft}
          disabled={disabled}
          onChange={(e) => setDraft(e.target.value.replace(/[^\d]/g, "").slice(0, 4))}
          onBlur={() => setDraft(String(commit(draft)))}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.blur();
            }
          }}
          className="min-w-0 flex-1 bg-transparent text-xl tabular-nums text-foreground dark:text-white outline-none"
          data-testid={testId}
        />
        <div className="flex items-center gap-1">
          <Button
            type="button"
            size="icon"
            variant="outline"
            className="h-8 w-8"
            disabled={disabled || value <= limits.min}
            onClick={() => step(-1)}
            aria-label={`Decrease ${label.toLowerCase()}`}
            data-testid={`${testId}-minus`}
          >
            <Minus className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="outline"
            className="h-8 w-8"
            disabled={disabled || value >= limits.max}
            onClick={() => step(1)}
            aria-label={`Increase ${label.toLowerCase()}`}
            data-testid={`${testId}-plus`}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
